import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/hooks/useSidebar';
import { NavItem } from '@/data/sidebar-data';
import { generateRouteUrl, navigateToRoute } from '@/utils/environment-urls';
import { 
  Diamond, 
  Trophy, 
  DollarSign,
  FileText, 
  Shield,
  X,
  Home,
  Users,
  BarChart3,
  AlertTriangle,
  UserPlus,
  Award,
  Settings,
  Bug,
  Gift,
  FileSpreadsheet,
  UserCheck,
  TrendingUp,
  DiamondIcon,
  Gem,
  ShoppingCart,
  LogOut,
  ShoppingBag,
  MessageSquareHeart,
  TicketIcon,
  Send
} from 'lucide-react';
import Ranking from '../Ranking';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar = ({ isOpen, onToggle }: SidebarProps) => {
  const { filteredNavItems, userName, userRole, isLoading } = useSidebar();
  const navigate = useNavigate();
  const [isRankingOpen, setIsRankingOpen] = useState(false);

  // Mapeamento de ícones
  const getIcon = (iconName?: string) => {
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      'home': Home,
      'users-group': Users,
      'shopping-card': ShoppingCart,
      'gem': Gem,
      'trophy': Trophy,
      'shopping-bag': ShoppingBag,
      'message-square-heart': MessageSquareHeart,
      'currency-dollar': DollarSign,
      'receipt-2': FileText,
      'report-money': BarChart3,
      'shield-check': Shield,
      'coin-off': AlertTriangle,
      'user-pentagon': UserCheck,
      'users-plus': UserPlus,
      'certificate-2': Award,
      'device-desktop-analytics': TrendingUp,
      'gift': Gift,
      'ticket': TicketIcon,
      'file-invoice': FileSpreadsheet,
      'settings': Settings,
      'bug': Bug,
      'send': Send,
    };

    const IconComponent = iconMap[iconName || ''] || Diamond;
    return IconComponent;
  };

  const franchiseName = localStorage.getItem("franquia") || "Matriz";

  const renderNavItem = (item: NavItem, index: number) => {
    // Se é uma categoria (navCap)
    if (item.navCap) {
      return (
        <div key={`cap-${index}`} className="mt-6 first:mt-0">
          <p className="text-xs font-semibold text-primary-foreground/50 uppercase tracking-wider mb-3">
            {item.navCap}
          </p>
        </div>
      );
    }

    // Se é um item de menu
    if (item.displayName && item.route) {
      const IconComponent = getIcon(item.iconName);
      
      // Determina se é uma rota do React ou Angular
      const isReactRoute = item.isReactRoute === true;
      
      // Se for rota do React, usa NavLink normal
      if (isReactRoute) {
        return (
          <>
            <NavLink
              key={`item-${index}`}
              to={item.route}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10",
                isActive && "bg-primary-foreground/20 text-primary-foreground font-medium"
              )}
              onClick={() => window.innerWidth < 1024 && onToggle()}
            >
              <IconComponent className="w-5 h-5" />
              <span>{item.displayName}</span>
            </NavLink>
            {item.route === '/minha-rede' && (
              // add ranking rede btn as link to open modal
              <NavLink
                to="#"
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                  "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                )}
                onClick={() => {
                  window.innerWidth < 1024 && onToggle();
                  setIsRankingOpen(true);
                }}
              >
                <TrendingUp className="w-5 h-5" />
                <span>Ranking de Indicações</span>
              </NavLink>
            )}
          </>
        );
      } else {
        // Se for rota do Angular, usa navegação externa
        const handleAngularNavigation = (e: React.MouseEvent) => {
          e.preventDefault();
          navigateToRoute(item.route!, false); // false = Angular route
          if (window.innerWidth < 1024) onToggle();
        };

        return (
          <a
            key={`item-${index}`}
            href={generateRouteUrl(item.route!, 'angular')}
            onClick={handleAngularNavigation}
            className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
          >
            <IconComponent className="w-5 h-5" />
            <span>{item.displayName}</span>
          </a>
        );
      }
    }

    return null;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-foreground/20 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-primary to-primary-hover transform transition-transform duration-300 ease-in-out z-50 flex flex-col",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Header */}
        <div className="p-6 border-b border-primary-foreground/10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <div className='flex items-center p-2 rounded-lg bg-white mb-2'>
                <img
                  src="/v2/logos/logo-mini.png"
                  alt="Logo X do Infinito"
                  className="w-8 h-8 object-contain mr-2"
                />
                <h1 className='text-lg font-bold text-primary'>X do Infinito</h1>
              </div>
               
              <p className="text-sm text-primary-foreground/70 mt-1">
                {franchiseName}
              </p>
            </div>
            <button
              onClick={onToggle}
              className="lg:hidden absolute right-1 text-primary-foreground hover:bg-primary-foreground/10 p-2 rounded-lg mt-[-90px]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Menu */}
        <nav 
          className="flex-1 overflow-y-auto p-4 space-y-1 custom-scrollbar"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
            </div>
          ) : (
            filteredNavItems.map((item, index) => renderNavItem(item, index))
          )}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-primary-foreground/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-foreground">
             {userName.charAt(0)}{userName.split(' ')[1]?.charAt(0) || ''}             </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-primary-foreground truncate">
             {userName}             </p>
              <p className="text-xs text-primary-foreground/60">{userRole}</p>
            </div>
            <button onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('userlogged');
                navigate('/authentication/side-login')
              }}
              className="ml-2 px-3 py-1 text-xs rounded bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 transition"
              title="Logout"
            >
              <LogOut className="w-4 h-4 inline-block mr-1" />
              Sair
            </button>
          </div>
        </div>
      </aside>
      <Ranking 
        isOpen={isRankingOpen} 
        onClose={() => setIsRankingOpen(false)} 
      />
    </>
  );
};

export default Sidebar;